/**
 * Lead Upload Component
 * Features: CSV/XLSX, dedup, full-name split, comprehensive column mapping,
 *           multi-phone cascade, cell prefix priority, bad phone filtering, phone type
 */

import { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, writeBatch, doc, getDoc, getDocs, query, addDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, IS_REHEARSAL } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useCredits } from '../../hooks/useCredits';
import { useEffectiveClientId, useImpersonation } from '../../hooks/useImpersonation';
import { PortalHeader } from '../Layout/PortalHeader';
import { VerifyBatchModal } from './VerifyBatchModal';
import * as XLSX from 'xlsx';

// How long to leave the success message visible before auto-redirecting
// to the dashboard. Long enough to read, short enough to feel snappy.
const POST_UPLOAD_REDIRECT_MS = 2500;

interface ColumnMapping {
  [key: string]: number | undefined;
}

// ---- Cell prefix set (loaded from /cell_prefixes.json) ----
let _cellPrefixes: Set<string> | null = null;

async function loadCellPrefixes(): Promise<Set<string>> {
  if (_cellPrefixes) return _cellPrefixes;
  try {
    const res = await fetch('/cell_prefixes.json');
    const arr = await res.json() as string[];
    _cellPrefixes = new Set(arr);
  } catch {
    _cellPrefixes = new Set();
  }
  return _cellPrefixes;
}

function isCellByPrefix(phone10: string, prefixes: Set<string>): boolean {
  return prefixes.has(phone10.slice(0, 6));
}

// ---- Server-side phone validation ----
const VALIDATE_URL = 'https://us-central1-retell-calling.cloudfunctions.net/validate-phones';

// 3-way status from validate-phones:
//   good_full           — in good cache AND reverse cache — ready to call, no verification needed
//   good_needs_reverse  — in good cache only — disc is free, reverse may still need to run (per-contact or opt-in)
//   bad                 — in bad cache — drop
//   unconfirmed         — not in any cache — needs disc (and maybe reverse)
//
// Independent flag:
//   na  — phone is in NA_Phones cache (non-accredited). The portal only acts
//         on this when client.settings.accreditedRequired === true; otherwise
//         the phone is callable.
export type VerificationStatus = 'good_full' | 'good_needs_reverse' | 'bad' | 'unconfirmed';

interface PhoneValidation {
  status: VerificationStatus;
  type: string; // wireless | landline | unknown
  na: boolean;  // non-accredited cache hit
  // Legacy field kept during transition
  bad?: boolean;
}

async function validatePhonesBatch(phones: string[]): Promise<Record<string, PhoneValidation>> {
  if (phones.length === 0) return {};
  // VALIDATE_URL is a hard-coded LIVE Cloud Function called with fetch (no SDK,
  // so no emulator switch applies). Never call it from the rehearsal bench.
  if (IS_REHEARSAL) return {};
  try {
    const res = await fetch(VALIDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phones }),
    });
    const data = await res.json();
    const raw: Record<string, { status?: string; type?: string; bad?: boolean; na?: boolean }> = data.results || {};
    // Normalize to the new shape (back-compat: old responses only had `bad`,
    // and pre-NA-rollout responses won't include `na` — default to false).
    const out: Record<string, PhoneValidation> = {};
    for (const [p, v] of Object.entries(raw)) {
      const status = (v.status as VerificationStatus) || (v.bad ? 'bad' : 'unconfirmed');
      out[p] = { status, type: v.type || 'unknown', na: v.na === true, bad: v.bad };
    }
    return out;
  } catch (err) {
    console.error('Phone validation failed:', err);
    return {};
  }
}

// ---- CSV parsing ----
function parseCSV(text: string): string[][] {
  const lines = text.split('\n');
  return lines.map(line => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') { inQuotes = !inQuotes; }
      else if (char === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
      else { current += char; }
    }
    result.push(current.trim());
    return result;
  }).filter(row => row.some(cell => cell));
}

// ---- Excel parsing ----
async function parseExcel(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: string[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false, defval: '' }) as string[][];
        const cleaned = rows.filter(row => row.some(cell => cell && String(cell).trim()))
          .map(row => row.map(cell => String(cell ?? '').trim()));
        if (cleaned.length < 1) { reject(new Error('File is empty')); return; }
        resolve(cleaned);
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// ---- Column alias mapping ----
const COLUMN_ALIASES: Record<string, string[]> = {
  phone: [
    'phone number','phone_number','phonenumber','cell phone','cell_phone','cellphone',
    'mobile phone','mobile phone number','mobile_number','home phone','home_phone',
    'work phone','work_phone','business phone','office phone','primary phone',
    'secondary phone','alternate phone','contact phone','main phone','telephone',
    'phone','phone 1','phone 2','phone#','phone_1','phones','phone1','phone2',
    'alt phone','alt_phone','appended phone','mobile','cell','home','work','tel','number',
  ],
  phone2: [
    'phone 2','phone_2','phone2','secondary phone','secondary_phone','alternate phone',
    'alt phone','alt_phone','home phone','home_phone','work phone','work_phone',
    'other phone','second phone','landline','office phone','business phone',
  ],
  phone3: [
    'phone 3','phone_3','phone3','work phone','work_phone','office phone',
    'business phone','landline phone','home phone','other phone',
  ],
  fullName: [
    'full name','full_name','fullname','first name last name','first last',
    'contact name','contact_name','customer name','client name','investor name',
    'lead name','prospect name','name','contact','client','customer',
  ],
  firstName: [
    'first name','first_name','firstname','contact first name','contact first',
    'lead first name','prospect first name','name first','name_first','fname','f_name','first',
  ],
  lastName: [
    'last name','last_name','lastname','surname','contact last name',
    'lead last name','prospect last name','name last','name_last','lname','l_name','last',
  ],
  email: [
    'email address','email_address','emailaddress','e-mail address','personal email address',
    'primary email address','home email','contact email','investor email','prospect email',
    'email','e-mail','email 1','emails','secondary email','appended email','mail',
  ],
  address: [
    'street address','street_address','address line 1','mailing address','mailing street',
    'home address 1','physical address','property address','full address','full_address',
    'address','address 1','address1','street','addr 1','addr1','addr',
  ],
  city: ['city','mailing city','property city','primary city','prospect city','billing_city','town/city','town'],
  state: ['state','state code','state or province','state/province','mailing state','property state','primary state','billing_state','province','region'],
  zip: ['zip code','zip_code','zipcode','postal code','postal_code','postcode','mailing zip','full zip','zip','zip 4','zip 5','postal'],
};

function autoMapColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const usedIndices = new Set<number>();
  const fieldPriority = ['phone', 'phone2', 'phone3', 'firstName', 'lastName', 'fullName', 'email', 'address', 'city', 'state', 'zip'];

  for (const field of fieldPriority) {
    const aliases = COLUMN_ALIASES[field] || [];
    for (const alias of aliases) {
      const idx = headers.findIndex((h, i) =>
        !usedIndices.has(i) && h.toLowerCase().replace(/[\s_]+/g, ' ').trim() === alias.toLowerCase().trim()
      );
      if (idx !== -1) { mapping[field] = idx; usedIndices.add(idx); break; }
    }
    if (mapping[field] === undefined) {
      for (const alias of aliases.slice(0, 5)) {
        const idx = headers.findIndex((h, i) =>
          !usedIndices.has(i) && h.toLowerCase().includes(alias.toLowerCase())
        );
        if (idx !== -1) { mapping[field] = idx; usedIndices.add(idx); break; }
      }
    }
  }

  // Auto-assign duplicate phone-like columns to phone2/phone3
  // Catches files with multiple columns named "Phone", "Phone", "Phone"
  const phoneAliases = COLUMN_ALIASES.phone || [];
  const phoneSlots: ('phone2' | 'phone3')[] = ['phone2', 'phone3'];
  for (const slot of phoneSlots) {
    if (mapping[slot] !== undefined) continue;
    for (const alias of phoneAliases) {
      const idx = headers.findIndex((h, i) =>
        !usedIndices.has(i) && h.toLowerCase().replace(/[\s_]+/g, ' ').trim() === alias.toLowerCase().trim()
      );
      if (idx !== -1) { mapping[slot] = idx; usedIndices.add(idx); break; }
    }
  }

  return mapping;
}

// ---- Name cleaning ----
const NAME_JUNK = new Set([
  'mr','mrs','miss','ms','dr','doctor','prof','rev','hon','sir',
  'mr.','mrs.','ms.','dr.','prof.','rev.','hon.','jr.','sr.',
  'jr','sr','ii','iii','iv','v','vi','md','dds','phd','cpa','esq',
  'trust','tr','tru','ttee','trustee','llc','inc','corp','ltd','co',
]);

function cleanNamePart(raw: string): string {
  if (!raw) return '';
  let s = raw.trim().replace(/["'`]/g, '').replace(/\s+/g, ' ').trim();
  s = s.split(' ').map(w => {
    if (NAME_JUNK.has(w.toLowerCase())) return '';
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).filter(Boolean).join(' ');
  return s;
}

function splitFullName(fullName: string): { firstName: string; lastName: string } {
  if (!fullName?.trim()) return { firstName: '', lastName: '' };
  const s = fullName.trim().replace(/\s+/g, ' ');
  if (s.includes(',')) {
    const parts = s.split(',').map(p => p.trim());
    return { firstName: cleanNamePart(parts[1] || ''), lastName: cleanNamePart(parts[0] || '') };
  }
  const words = s.split(' ');
  if (words.length === 1) return { firstName: cleanNamePart(words[0]), lastName: '' };
  return { firstName: cleanNamePart(words[0]), lastName: cleanNamePart(words.slice(1).join(' ')) };
}

// ---- Phone cleaning ----
const TOLL_FREE = new Set(['800', '833', '844', '855', '866', '877', '888']);

function cleanPhone10(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  if (d.length !== 10) return '';
  if (/(\d)\1{4,}/.test(d)) return '';
  const area = d.slice(0, 3);
  const exchange = d.slice(3, 6);
  // Area code: first digit 2-9, not N11 (x11), not 99x
  if (/[01]/.test(area[0])) return '';
  if (area[1] === '1' && area[2] === '1') return '';
  if (area[0] === '9' && area[1] === '9') return '';
  // Exchange: first digit 2-9, not N11 (x11)
  if (/[01]/.test(exchange[0])) return '';
  if (exchange[1] === '1' && exchange[2] === '1') return '';
  // Toll-free
  if (TOLL_FREE.has(area)) return '';
  return d;
}

// ---- Phone selection: pick best phone from up to 3 columns ----
//
// When `cellOnlyMode` is true, hard-reject phones we know are landline/voip
// from the validate-phones cache. For unknown-type phones, require the first
// 6 digits to match a known cell prefix. This removes landlines BEFORE
// disconnect — applies to both already-good and unconfirmed phones.
//
// When `accreditedRequired` is true, also reject phones flagged in the NA
// (non-accredited) cache. NA flag is independent of bad/good status — a
// `good_full` phone can still be NA, in which case an accredited-required
// client should drop it before dialing.
function selectBestPhone(
  row: string[],
  phoneIndices: (number | undefined)[],
  cellPrefixes: Set<string>,
  validationResults: Record<string, PhoneValidation>,
  cellOnlyMode: boolean = false,
  accreditedRequired: boolean = false,
): string {
  const candidates: { phone10: string; score: number }[] = [];

  for (const idx of phoneIndices) {
    if (idx === undefined) continue;
    const raw = row[idx] || '';
    const phone10 = cleanPhone10(raw);
    if (!phone10) continue;

    const validation = validationResults[phone10];
    // Skip phones confirmed bad (in the cached bad list)
    if (validation?.status === 'bad' || validation?.bad === true) continue;

    // Skip phones in the non-accredited cache when this client requires accreditation.
    if (accreditedRequired && validation?.na === true) continue;

    // Cell-only filter (applied to all rows good or unconfirmed):
    //   - Reject confirmed landline/voip from cache
    //   - Reject unknown-type phones whose first 6 digits don't match cell prefix
    //   - Keep wireless OR cell-prefix-matched phones
    if (cellOnlyMode) {
      const t = validation?.type;
      if (t === 'landline' || t === 'voip') continue;
      if (t !== 'wireless') {
        // unknown or missing — require cell prefix match
        if (!isCellByPrefix(phone10, cellPrefixes)) continue;
      }
    }

    let score = 0;
    // Prefer already-fully-verified phones first — zero future work needed
    if (validation?.status === 'good_full') score += 20;
    else if (validation?.status === 'good_needs_reverse') score += 15;
    // Prefer wireless/cell
    if (validation?.type === 'wireless') score += 10;
    else if (isCellByPrefix(phone10, cellPrefixes)) score += 8;
    else if (validation?.type === 'landline') score += 2;
    else score += 5; // unknown type, middle priority

    // Prefer earlier columns (primary > secondary > tertiary)
    score -= candidates.length * 0.1;

    candidates.push({ phone10, score });
  }

  if (candidates.length === 0) return '';
  candidates.sort((a, b) => b.score - a.score);
  return candidates[0].phone10;
}

// ---- Component ----
const FIELD_LABELS: Record<string, { label: string; required: boolean }> = {
  phone: { label: 'Phone (Primary)', required: true },
  phone2: { label: 'Phone (Secondary)', required: false },
  phone3: { label: 'Phone (Tertiary)', required: false },
  firstName: { label: 'First Name', required: false },
  lastName: { label: 'Last Name', required: false },
  fullName: { label: 'Full Name (auto-splits)', required: false },
  email: { label: 'Email', required: false },
  address: { label: 'Address', required: false },
  city: { label: 'City', required: false },
  state: { label: 'State', required: false },
  zip: { label: 'Zip', required: false },
};

const FIELD_ORDER = ['phone', 'phone2', 'phone3', 'firstName', 'lastName', 'fullName', 'email', 'address', 'city', 'state', 'zip'];

interface PendingVerification {
  counts: { good_full: number; good_needs_reverse: number; bad: number; unconfirmed: number };
  sourceFile: string;
  leadRows: Array<{
    phone: string; firstName: string; lastName: string; email: string;
    address: string; city: string; state: string; zip: string;
    sourceFile: string;
  }>;
}

/**
 * Build the unique sourceFile key for an upload. Format:
 *   `${userLabelOrFileName} (YYYY-MM-DD HH:mm:ss)`
 * The timestamp is always appended so two uploads with the same name stay
 * distinct in the delete-by-file picker.
 */
function buildSourceFile(userLabel: string, fallbackFileName: string): string {
  const base = (userLabel || '').trim() || (fallbackFileName || 'upload').trim();
  const safeBase = base.replace(/\s+/g, ' ').slice(0, 80);
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const ts = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
             `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  return `${safeBase} (${ts})`;
}

export function Leads() {
  const { user } = useAuth();
  // When admin is impersonating, every Firestore write needs to land in the
  // impersonated client's collection — not the admin's. Falls back to user.uid
  // when not impersonating, so normal client behavior is unchanged.
  const effectiveClientId = useEffectiveClientId();
  const { isImpersonating, impersonatedClientName, impersonatedClientId } = useImpersonation();
  const { billingType } = useCredits();
  const navigate = useNavigate();
  const [cellPrefixes, setCellPrefixes] = useState<Set<string>>(new Set());
  const [file, setFile] = useState<File | null>(null);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [noHeader, setNoHeader] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [preview, setPreview] = useState<string[][]>([]);
  const [allRows, setAllRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [pendingVerification, setPendingVerification] = useState<PendingVerification | null>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [cellOnly, setCellOnly] = useState(false);
  const [showCellOnlyConfirm, setShowCellOnlyConfirm] = useState(false);
  const [uploadLabel, setUploadLabel] = useState('');
  // Per-call name verification opt-outs. Only shown to per-call clients.
  //   verifyNames   — master switch for the reference-dictionary check (default OFF
  //                   for per-call: those clients are not required to confirm
  //                   names match the lead — opt-in if they want the extra rigor)
  //   skipLastName  — let records without a verified (or any) last name through
  const [verifyNames, setVerifyNames] = useState(false);
  const [skipLastName, setSkipLastName] = useState(false);
  // accreditedRequired drives non-accredited phone removal at upload time.
  // Read once from clients/{uid}.settings — stays cached for the session.
  const [accreditedRequired, setAccreditedRequired] = useState(false);
  const isPerCall = billingType !== 'per_contact';

  useEffect(() => { loadCellPrefixes().then(setCellPrefixes); }, []);

  // Read client.settings.accreditedRequired so upload-time scrub can drop NA
  // phones for clients that require accreditation. Default false on missing
  // field — non-accredited-required clients see no behavior change.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const settingsTarget = isImpersonating && impersonatedClientId
          ? impersonatedClientId
          : (effectiveClientId || user.uid);
        const snap = await getDoc(doc(db, 'clients', settingsTarget));
        const settings = (snap.data()?.settings || {}) as Record<string, unknown>;
        setAccreditedRequired(settings.accreditedRequired === true);
      } catch (err) {
        console.error('Failed to read accreditedRequired:', err);
      }
    })();
  }, [user]);

  // Apply header mode to raw rows
  const applyHeaderMode = (rows: string[][], headerless: boolean) => {
    if (rows.length === 0) return;
    if (headerless) {
      const colCount = rows[0].length;
      const genHeaders = Array.from({ length: colCount }, (_, i) =>
        `Column ${String.fromCharCode(65 + Math.min(i, 25))}${i >= 26 ? (i - 25).toString() : ''}`
      );
      setHeaders(genHeaders);
      setPreview(rows.slice(0, 5));
      setAllRows(rows);
      setMapping({});  // no auto-map possible — user must map manually
    } else {
      if (rows.length < 2) { setResult('Error: File must have header + data rows'); return; }
      setHeaders(rows[0]);
      setPreview(rows.slice(1, 6));
      setAllRows(rows.slice(1));
      setMapping(autoMapColumns(rows[0]));
    }
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setResult(null); setProgress(''); setNoHeader(false);
    try {
      let rows: string[][];
      const ext = f.name.toLowerCase().split('.').pop();
      if (ext === 'csv' || ext === 'txt') { rows = parseCSV(await f.text()); }
      else if (ext === 'xls' || ext === 'xlsx') { rows = await parseExcel(f); }
      else { setResult('Error: Unsupported file type. Use .csv, .xls, or .xlsx'); return; }
      if (rows.length < 1) { setResult('Error: File is empty'); return; }
      setRawRows(rows);
      // Auto-detect headerless files: if first row has no column that matches any known alias, treat as no header
      const firstRow = rows[0];
      const hasRecognizedHeader = firstRow.some(cell => {
        const lower = cell.toLowerCase().replace(/[\s_]+/g, ' ').trim();
        return Object.values(COLUMN_ALIASES).flat().some(alias => alias.toLowerCase().trim() === lower);
      });
      const detectedNoHeader = !hasRecognizedHeader;
      setNoHeader(detectedNoHeader);
      applyHeaderMode(rows, detectedNoHeader);
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed to read file'));
    }
  };

  const toggleNoHeader = () => {
    const next = !noHeader;
    setNoHeader(next);
    applyHeaderMode(rawRows, next);
  };

  // Accepts an optional cellOnly override because state updates from setCellOnly
  // don't apply on the same tick as a button click — the confirm popup passes
  // the user's choice explicitly so we don't read stale state.
  const handleUpload = async (cellOnlyOverride?: boolean) => {
    if (!file || !user || allRows.length === 0) return;
    if (mapping.phone === undefined) { setResult('Error: Please map the Phone (Primary) column'); return; }
    const hasNames = mapping.firstName !== undefined || mapping.fullName !== undefined;
    if (!hasNames) { setResult('Error: Please map First Name + Last Name, or a Full Name column'); return; }

    const effectiveCellOnly = cellOnlyOverride !== undefined ? cellOnlyOverride : cellOnly;
    const sourceFile = buildSourceFile(uploadLabel, file.name);

    setUploading(true); setResult(null);

    try {
      // Resolve target client UID once, log it, and guard against the silent
      // fallback that previously sent leads to the admin's collection when
      // impersonation context was missing. If the admin started an
      // impersonation session, we MUST write to the impersonated client —
      // never the admin's UID.
      const targetClientId = isImpersonating ? (impersonatedClientId || '') : (effectiveClientId || user.uid);
      console.log('[Leads upload] auth.uid=', user.uid,
                  ' effectiveClientId=', effectiveClientId,
                  ' isImpersonating=', isImpersonating,
                  ' impersonatedClientId=', impersonatedClientId,
                  ' → writing to clients/' + targetClientId);
      if (isImpersonating && (!impersonatedClientId || targetClientId === user.uid)) {
        throw new Error(
          'Impersonation context is invalid — refusing to write leads to the admin account. ' +
          'Stop impersonation, refresh the page, and re-enter as the client.'
        );
      }
      const leadsRef = collection(db, 'clients', targetClientId, 'leads');

      // Phase 1: Load existing phones for dedup
      setProgress('Checking for duplicates...');
      const existingSnapshot = await getDocs(query(leadsRef));
      const existingPhones = new Set<string>();
      existingSnapshot.forEach(d => { const p = d.data().phone as string; if (p) existingPhones.add(p); });

      // Phase 2: Collect all candidate phones for server-side validation
      setProgress('Collecting phone numbers...');
      const phoneIndices = [mapping.phone, mapping.phone2, mapping.phone3];
      const allPhones = new Set<string>();
      for (const row of allRows) {
        for (const idx of phoneIndices) {
          if (idx === undefined) continue;
          const p = cleanPhone10(row[idx] || '');
          if (p) allPhones.add(p);
        }
      }

      // Phase 3: Validate phones server-side — returns 3-way status
      setProgress(`Validating ${allPhones.size} phone numbers...`);
      let validationResults: Record<string, PhoneValidation> = {};
      const phoneBatches: string[][] = [];
      const phoneArr = Array.from(allPhones);
      for (let i = 0; i < phoneArr.length; i += 5000) {
        phoneBatches.push(phoneArr.slice(i, i + 5000));
      }
      for (const batch of phoneBatches) {
        const batchResult = await validatePhonesBatch(batch);
        validationResults = { ...validationResults, ...batchResult };
      }

      // Count statuses for summary
      const statusCounts = { good_full: 0, good_needs_reverse: 0, bad: 0, unconfirmed: 0 };
      // NA count is independent of status (a good_full phone can also be NA).
      // We only act on it when accreditedRequired is true; the count is shown
      // for transparency either way so the user can see what the cache contained.
      let naCount = 0;
      for (const v of Object.values(validationResults)) {
        if (v.status in statusCounts) statusCounts[v.status as keyof typeof statusCounts]++;
        if (v.na) naCount++;
      }
      setProgress(`Found ${statusCounts.good_full} verified, ${statusCounts.bad} bad, ${statusCounts.unconfirmed + statusCounts.good_needs_reverse} unconfirmed`);

      // Phase 3b: Server-side name validation (reference dictionary + swap detection)
      setProgress('Validating names...');
      const nameInputs: Array<{ firstName: string; lastName: string; fullName?: string; index: number }> = [];
      for (let i = 0; i < allRows.length; i++) {
        const row = allRows[i];
        let fn = '', ln = '';
        let fullNameRaw: string | undefined;
        if (mapping.fullName !== undefined) {
          fullNameRaw = (row[mapping.fullName] || '').trim();
          const split = splitFullName(row[mapping.fullName] || '');
          fn = split.firstName; ln = split.lastName;
        }
        if (mapping.firstName !== undefined && row[mapping.firstName]) fn = cleanNamePart(row[mapping.firstName]);
        if (mapping.lastName !== undefined && row[mapping.lastName]) ln = cleanNamePart(row[mapping.lastName]);
        nameInputs.push({ firstName: fn, lastName: ln, fullName: fullNameRaw, index: i });
      }

      // Call validateNames in batches of 10,000.
      // Per-call clients can opt out of the reference check entirely
      // (skipNameCheck) or just the last-name portion (skipLastNameCheck).
      // Per-contact clients always run the full check — no opt-out.
      const skipNameCheck = isPerCall && !verifyNames;
      const skipLastNameCheck = isPerCall && skipLastName;
      const nameResults = new Map<number, { firstName: string; lastName: string; valid: boolean; reason: string }>();
      const validateNamesFn = httpsCallable<
        { names: typeof nameInputs; skipNameCheck?: boolean; skipLastNameCheck?: boolean },
        { results: Array<{ index: number; firstName: string; lastName: string; valid: boolean; swapped: boolean; reason: string }> }
      >(functions, 'validateNames');
      for (let i = 0; i < nameInputs.length; i += 10000) {
        const chunk = nameInputs.slice(i, i + 10000);
        try {
          const resp = await validateNamesFn({ names: chunk, skipNameCheck, skipLastNameCheck });
          for (const r of resp.data.results) {
            nameResults.set(r.index, { firstName: r.firstName, lastName: r.lastName, valid: r.valid, reason: r.reason });
          }
        } catch (err) {
          console.error('Name validation batch failed, allowing all through:', err);
          // Graceful degradation: if validation fails, allow all names through with client-side cleaning
          for (const n of chunk) {
            nameResults.set(n.index, { firstName: n.firstName, lastName: n.lastName, valid: true, reason: '' });
          }
        }
      }
      const nameInvalidCount = [...nameResults.values()].filter(r => !r.valid).length;
      if (nameInvalidCount > 0) {
        setProgress(`Name validation: ${nameResults.size - nameInvalidCount} valid, ${nameInvalidCount} filtered`);
      }

      // Phase 4: Partition rows into write-now vs queue-for-verification
      const seenInUpload = new Set<string>();
      let addedNow = 0, skipped = 0, dupeSkipped = 0, cellOnlyExcluded = 0, nameFiltered = 0;
      const queuedForVerification: Array<{
        phone: string;
        firstName: string;
        lastName: string;
        email: string;
        address: string;
        city: string;
        state: string;
        zip: string;
        originalStatus?: 'unconfirmed' | 'good_needs_reverse';
        sourceFile: string;
      }> = [];
      let batch = writeBatch(db);
      let batchCount = 0;

      for (let rowIdx = 0; rowIdx < allRows.length; rowIdx++) {
        const row = allRows[rowIdx];

        // Check server-side name validation result first
        const nameResult = nameResults.get(rowIdx);
        if (nameResult && !nameResult.valid) {
          nameFiltered++;
          continue;
        }

        // First pass: get phone WITHOUT cell-only filter to know if we'd otherwise have it
        const phoneWithoutFilter = effectiveCellOnly
          ? selectBestPhone(row, phoneIndices, cellPrefixes, validationResults, false, accreditedRequired)
          : null;
        const phone10 = selectBestPhone(row, phoneIndices, cellPrefixes, validationResults, effectiveCellOnly, accreditedRequired);
        if (!phone10) {
          // If cellOnly excluded a phone we would have otherwise taken, count it separately
          if (effectiveCellOnly && phoneWithoutFilter) {
            cellOnlyExcluded++;
          } else {
            skipped++;
          }
          continue;
        }

        const phoneE164 = `+1${phone10}`;
        if (existingPhones.has(phoneE164)) { dupeSkipped++; continue; }
        if (seenInUpload.has(phoneE164)) { dupeSkipped++; continue; }
        seenInUpload.add(phoneE164);

        // Use server-validated (cleaned + swap-corrected) names
        const firstName = nameResult?.firstName || '';
        const lastName = nameResult?.lastName || '';

        const leadRow = {
          phone: phoneE164,
          firstName,
          lastName,
          email: mapping.email !== undefined ? (row[mapping.email] || '').trim() : '',
          address: mapping.address !== undefined ? (row[mapping.address] || '').trim() : '',
          city: mapping.city !== undefined ? (row[mapping.city] || '').trim() : '',
          state: mapping.state !== undefined ? (row[mapping.state] || '').trim() : '',
          zip: mapping.zip !== undefined ? (row[mapping.zip] || '').trim() : '',
        };

        const status = validationResults[phone10]?.status || 'unconfirmed';
        // Per-call clients are not required to confirm name match — reverse
        // verification is opt-in via the modal. Treat good_needs_reverse the
        // same as good_full so they get written directly as ready leads
        // instead of being queued for a verification step the user never asked
        // for. Per-contact clients still queue good_needs_reverse because
        // reverse is mandatory for that billing type.
        const treatAsGood = status === 'good_full' || (status === 'good_needs_reverse' && isPerCall);
        if (treatAsGood) {
          batch.set(doc(leadsRef), {
            ...leadRow,
            status: 'pending', run: 1, attemptCount: 0, nextEligibleCallAt: null, createdAt: new Date(),
            verifiedBy: status === 'good_full' ? 'cache' : 'cache_disc_only',
            sourceFile,
          });
          addedNow++; batchCount++;
          if (batchCount >= 500) {
            setProgress(`Writing verified leads... ${addedNow}`);
            await batch.commit(); batch = writeBatch(db); batchCount = 0;
          }
        } else {
          // unconfirmed OR (per-contact good_needs_reverse) → queue for modal
          // Stash the original validation status so runDeferredVerification
          // can compute cost without re-validating later.
          queuedForVerification.push({
            ...leadRow,
            originalStatus: status === 'good_needs_reverse' ? 'good_needs_reverse' : 'unconfirmed',
            sourceFile,
          });
        }
      }
      if (batchCount > 0) await batch.commit();

      // Record an upload event so the Support Chat's explain_last_upload tool
      // can answer "why were some of my leads filtered?" with real numbers.
      // Uses add() (auto-id) so re-uploading doesn't overwrite history.
      try {
        await addDoc(collection(db, 'clients', targetClientId, 'uploadEvents'), {
          uploadedAt: new Date(),
          totalRows: allRows.length,
          kept: addedNow + queuedForVerification.length,
          addedImmediately: addedNow,
          queuedForVerification: queuedForVerification.length,
          filterCounts: {
            badPhone: statusCounts.bad + skipped,
            duplicate: dupeSkipped,
            nameFiltered: nameFiltered,
            nonCell: cellOnlyExcluded,
            // Only counted as a filter when this client requires accreditation
            // (otherwise NA phones are kept as callable).
            nonAccredited: accreditedRequired ? naCount : 0,
          },
          filePath: file?.name ?? null,
          sourceFile,
          userAgent: navigator.userAgent.slice(0, 256),
          cellOnly: effectiveCellOnly,
          readyInRun: 1,
        });
      } catch (evtErr) {
        // Non-fatal: the upload succeeded even if telemetry write failed
        console.warn('Failed to log upload event for support chat:', evtErr);
      }

      // Build summary
      const summaryParts: string[] = [];
      if (addedNow > 0) summaryParts.push(`${addedNow} leads added (already verified)`);
      if (queuedForVerification.length > 0) summaryParts.push(`${queuedForVerification.length} pending verification`);
      if (statusCounts.bad > 0) summaryParts.push(`${statusCounts.bad} bad phones filtered`);
      if (accreditedRequired && naCount > 0) summaryParts.push(`${naCount} non-accredited phones filtered`);
      if (nameFiltered > 0) summaryParts.push(`${nameFiltered} bad names filtered`);
      if (cellOnlyExcluded > 0) summaryParts.push(`${cellOnlyExcluded} non-cell phones excluded`);
      if (skipped > 0) summaryParts.push(`${skipped} invalid phones`);
      if (dupeSkipped > 0) summaryParts.push(`${dupeSkipped} duplicates`);

      if (queuedForVerification.length > 0) {
        // Stash for modal pickup
        setPendingVerification({
          counts: statusCounts,
          leadRows: queuedForVerification,
          sourceFile,
        });
        setShowVerifyModal(true);
        setResult(summaryParts.join(' | ') + '.');
      } else {
        setResult(summaryParts.join(' | ') + '.');
        setFile(null); setRawRows([]); setNoHeader(false); setHeaders([]); setPreview([]); setAllRows([]); setProgress(''); setUploadLabel('');
        // Auto-redirect to the dashboard so the user can immediately see
        // their leads in the run grid. Brief delay lets them read the
        // success summary first.
        window.setTimeout(() => navigate('/'), POST_UPLOAD_REDIRECT_MS);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setResult('Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setProgress('');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container">
        {/* Impersonation banner — surfaces the active impersonation target so
            admins can VERIFY at a glance that the upload is going to the right
            client, not their own admin account. Sits above the cleaning notice. */}
        {isImpersonating && (
          <div style={{
            padding: '14px 20px', marginBottom: '16px', borderRadius: '8px',
            background: 'rgba(220, 38, 38, 0.12)', border: '2px solid #dc2626',
            color: '#fecaca', fontSize: '0.95rem', lineHeight: 1.5,
          }}>
            <div style={{ color: '#fca5a5', fontWeight: 700, marginBottom: '4px' }}>
              ⚠️ Admin Impersonation Active
            </div>
            <div>
              Uploads on this page will be written to <strong>{impersonatedClientName || 'the impersonated client'}</strong>
              {' '}(<code style={{ fontSize: '0.85em' }}>{impersonatedClientId}</code>),
              not your admin account. If this is wrong, click "Stop Impersonation" in the dashboard banner before uploading.
            </div>
          </div>
        )}

        {/* Data cleaning notice — shown above the upload area */}
        <div style={{
          padding: '16px 20px', marginBottom: '20px', borderRadius: '8px',
          background: 'rgba(200, 168, 78, 0.08)', border: '1px solid rgba(200, 168, 78, 0.3)',
          color: 'var(--gray-200)', fontSize: '0.9rem', lineHeight: 1.6,
        }}>
          <div style={{ color: 'var(--gold)', fontWeight: 700, marginBottom: '8px' }}>About lead cleaning</div>
          <p style={{ margin: '0 0 8px' }}>
            <strong>JAD Enterprises leads</strong> are ready to call immediately &mdash; no verification needed.
          </p>
          <p style={{ margin: '0 0 8px' }}>
            For leads from other sources, we first suppress known-disconnected phones, automated phone systems,
            dial-by-name directories, DNC requests, known complainers and litigators, government officials,
            regulators, deceased individuals, and toll-free numbers. We then check against our confirmed
            in-service database at no cost to you.
          </p>
          <p style={{ margin: '0 0 8px' }}>
            Any phones we can&rsquo;t confirm go through a verification process:
            <br />
            &bull; <strong>Disconnect check</strong> &mdash; we test the line to confirm it&rsquo;s still active.
            <br />
            &bull; <strong>Reverse phone check</strong> &mdash; we look up the registered name to confirm it matches your list.
          </p>
          <p style={{ margin: '0 0 8px' }}>
            Per-call credit clients pay for the disconnect check &mdash; reverse phone is optional.
            Per-contact credit clients run both automatically. You can also filter results to cell phones only.
          </p>
          <p style={{ margin: '0 0 8px' }}>
            We also validate and clean all names during upload:
            <br />
            &bull; Titles, suffixes, and credentials are removed (Dr., Jr., III, CPA, etc.)
            <br />
            &bull; Business and entity names are filtered out (LLC, Corp, Trust, etc.)
            <br />
            &bull; Names are verified against a 1.8M+ person name reference database
            <br />
            &bull; Swapped first/last names are automatically detected and corrected
            <br />
            &bull; Placeholder values (N/A, Unknown, Test) and junk entries are removed
          </p>
          <p style={{ margin: 0 }}>
            Invalid names are excluded from your list so you only pay for real, callable contacts.
          </p>
        </div>
        {!file ? (
          <label className="upload-area">
            <input type="file" accept=".csv,.xls,.xlsx,.txt" onChange={handleFileSelect} />
            <div>
              <div style={{ fontSize: '1.25rem', marginBottom: '8px', color: 'var(--gold)' }}>Click to select file</div>
              <div style={{ color: 'var(--gray-400)' }}>CSV, XLS, XLSX &mdash; phone, name, address columns</div>
              <div style={{ color: 'var(--gray-500)', fontSize: '0.8rem', marginTop: '8px' }}>
                Bad phones filtered | Names validated | Cell phones preferred | Duplicates removed
              </div>
            </div>
          </label>
        ) : (
          <>
            <h2 className="section-title">Map Your Columns</h2>
            <p style={{ color: 'var(--gray-400)', marginBottom: '12px', fontSize: '0.9rem' }}>
              {allRows.length} rows. Map Phone + a name column. Multiple phone columns = best phone auto-selected (cell preferred).
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: 'pointer', color: 'var(--gray-300)', fontSize: '0.9rem' }}>
              <input type="checkbox" checked={noHeader} onChange={toggleNoHeader} style={{ accentColor: 'var(--gold)' }} />
              File has no header row (first row is data)
            </label>

            {/* Name this upload — drives the delete-by-file picker later */}
            <div style={{
              padding: '14px 18px', marginBottom: '20px', borderRadius: '10px',
              background: 'rgba(46, 163, 242, 0.08)', border: '2px solid rgba(46, 163, 242, 0.35)',
            }}>
              <label style={{ display: 'block', color: 'var(--gold)', fontWeight: 700, marginBottom: '8px', fontSize: '0.95rem' }}>
                Name this upload (optional)
              </label>
              <input
                type="text"
                value={uploadLabel}
                onChange={e => setUploadLabel(e.target.value)}
                placeholder={file?.name || 'e.g. Florida Investors March'}
                disabled={uploading}
                maxLength={80}
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '0.95rem',
                }}
              />
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '6px', lineHeight: 1.4 }}>
                The date and time are added automatically so each upload stays unique.
                Leave blank to use the file name. You&rsquo;ll be able to delete this specific upload by name later.
              </div>
            </div>

            {/* Cell phones only toggle — moved to top of mapping section so users see it BEFORE scrolling */}
            <div style={{
              padding: '14px 18px', marginBottom: '20px', borderRadius: '10px',
              background: 'rgba(200, 168, 78, 0.10)', border: '2px solid rgba(200, 168, 78, 0.45)',
            }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: 'var(--gray-100)', fontSize: '0.95rem' }}>
                <input
                  type="checkbox"
                  checked={cellOnly}
                  disabled={uploading}
                  onChange={e => setCellOnly(e.target.checked)}
                  style={{ accentColor: 'var(--gold)', marginTop: '3px', width: '18px', height: '18px' }}
                />
                <span>
                  <strong style={{ color: 'var(--gold)', fontSize: '1rem' }}>Cell phones only</strong>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-300)', marginTop: '4px', lineHeight: 1.5 }}>
                    Excludes landlines (and phones we can&rsquo;t confirm as cell) before any verification runs.
                    Applied to <strong>all rows</strong> &mdash; both already-verified and unconfirmed.
                    For unknown phone types we check the first 6 digits against the cell prefix list.
                    You&rsquo;ll be asked to confirm before the upload starts.
                  </div>
                </span>
              </label>
            </div>

            {/* Per-call name verification opt-outs. Hidden for per-contact clients,
                who always run the full first+last reference check. */}
            {isPerCall && (
              <div style={{
                padding: '14px 18px', marginBottom: '20px', borderRadius: '10px',
                background: 'rgba(46, 163, 242, 0.08)', border: '2px solid rgba(46, 163, 242, 0.35)',
              }}>
                <div style={{ color: 'var(--gold)', fontWeight: 700, marginBottom: '10px', fontSize: '0.95rem' }}>
                  Name verification (pay-per-call only)
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: 'var(--gray-100)', fontSize: '0.95rem', marginBottom: '10px' }}>
                  <input
                    type="checkbox"
                    checked={verifyNames}
                    disabled={uploading}
                    onChange={e => setVerifyNames(e.target.checked)}
                    style={{ accentColor: 'var(--gold)', marginTop: '3px', width: '18px', height: '18px' }}
                  />
                  <span>
                    <strong>Verify names against our reference database</strong>
                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-300)', marginTop: '4px', lineHeight: 1.5 }}>
                      On by default. Uncheck to process records <strong>without checking</strong> either first
                      or last name &mdash; cleaning (title/junk removal) still runs, but no reference lookup
                      is performed.
                    </div>
                  </span>
                </label>
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                  cursor: verifyNames ? 'pointer' : 'not-allowed',
                  color: verifyNames ? 'var(--gray-100)' : 'var(--gray-500)',
                  fontSize: '0.95rem',
                }}>
                  <input
                    type="checkbox"
                    checked={skipLastName}
                    disabled={uploading || !verifyNames}
                    onChange={e => setSkipLastName(e.target.checked)}
                    style={{ accentColor: 'var(--gold)', marginTop: '3px', width: '18px', height: '18px' }}
                  />
                  <span>
                    <strong>Skip last-name verification</strong>
                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-300)', marginTop: '4px', lineHeight: 1.5 }}>
                      Allows records with no last name &mdash; or a last name that doesn&rsquo;t match our
                      reference database &mdash; to pass through. First-name verification still runs.
                    </div>
                  </span>
                </label>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              {FIELD_ORDER.map(field => {
                const info = FIELD_LABELS[field];
                return (
                  <div key={field} className="form-group" style={{ marginBottom: 0 }}>
                    <label>{info.label} {info.required && <span style={{ color: 'var(--red)' }}>*</span>}</label>
                    <select
                      value={mapping[field] ?? ''}
                      onChange={e => setMapping({ ...mapping, [field]: e.target.value ? parseInt(e.target.value) : undefined })}
                    >
                      <option value="">-- Not mapped --</option>
                      {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>

            {mapping.fullName !== undefined && mapping.firstName === undefined && (
              <p style={{ color: 'var(--gold)', fontSize: '0.85rem', marginBottom: '16px' }}>
                Full Name mapped — auto-splits into First/Last. Handles "First Last" and "Last, First".
              </p>
            )}
            {(mapping.phone2 !== undefined || mapping.phone3 !== undefined) && (
              <p style={{ color: 'var(--gold)', fontSize: '0.85rem', marginBottom: '16px' }}>
                Multiple phones mapped — best phone auto-selected per row (cell preferred over landline, bad phones removed).
              </p>
            )}

            <h2 className="section-title">Preview (First 5 rows)</h2>
            <div className="table-container" style={{ marginBottom: '24px', overflowX: 'auto' }}>
              <table>
                <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                <tbody>{preview.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => {
                  // Always show the confirm popup before upload — it's a single
                  // fast click that ensures the user sees the cell-only option.
                  if (!file || allRows.length === 0) return;
                  if (mapping.phone === undefined) { setResult('Error: Please map the Phone (Primary) column'); return; }
                  const hasNames = mapping.firstName !== undefined || mapping.fullName !== undefined;
                  if (!hasNames) { setResult('Error: Please map First Name + Last Name, or a Full Name column'); return; }
                  setShowCellOnlyConfirm(true);
                }}
                disabled={uploading}
                className="btn btn-primary"
              >
                {uploading ? 'Processing...' : 'Upload Leads'}
              </button>
              <button onClick={() => { setFile(null); setRawRows([]); setNoHeader(false); setHeaders([]); setPreview([]); setAllRows([]); setResult(null); setProgress(''); setCellOnly(false); setVerifyNames(true); setSkipLastName(false); setUploadLabel(''); }}
                className="btn btn-secondary" disabled={uploading}>Cancel</button>
              {progress && <span style={{ color: 'var(--gold)', fontSize: '0.85rem' }}>{progress}</span>}
            </div>
          </>
        )}

        {result && (
          <div style={{
            marginTop: '24px', padding: '16px', borderRadius: '8px',
            background: result.startsWith('Error') || result.startsWith('Upload failed') ? 'var(--red-bg)' : 'var(--green-bg)',
            color: result.startsWith('Error') || result.startsWith('Upload failed') ? 'var(--red)' : 'var(--green)',
            border: `1px solid ${result.startsWith('Error') || result.startsWith('Upload failed') ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`
          }}>{result}</div>
        )}
      </div>

      {/* Cell-only confirm popup — fast yes/no before upload kicks off */}
      {showCellOnlyConfirm && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div className="modal-content" style={{
            background: 'var(--bg-primary, #1e1e1e)', borderRadius: '12px',
            padding: '32px', maxWidth: '480px', width: '100%',
            border: '2px solid rgba(200, 168, 78, 0.5)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <h2 style={{ margin: '0 0 16px', color: 'var(--gold)' }}>Cell phones only?</h2>
            <p style={{ color: 'var(--gray-200)', lineHeight: 1.6, margin: '0 0 16px' }}>
              You&rsquo;re about to upload <strong>{allRows.length.toLocaleString()}</strong> rows.
              Do you want to <strong>exclude landlines</strong> from this upload?
            </p>
            <p style={{ color: 'var(--gray-400)', lineHeight: 1.6, margin: '0 0 20px', fontSize: '0.9rem' }}>
              When enabled, we drop any phone we know is a landline (or VoIP) and any phone whose first 6 digits don&rsquo;t match a known cell prefix.
              Applies to <strong>all rows</strong> in this upload, even already-verified ones.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setCellOnly(false);
                  setShowCellOnlyConfirm(false);
                  // Run upload with cellOnly=false (state hasn't updated yet on this tick,
                  // so handleUpload reads the prior value — pass an explicit override)
                  handleUpload(false);
                }}
                className="btn btn-secondary"
              >
                No &mdash; keep all phones
              </button>
              <button
                onClick={() => {
                  setCellOnly(true);
                  setShowCellOnlyConfirm(false);
                  handleUpload(true);
                }}
                className="btn btn-primary"
              >
                Yes &mdash; cell phones only
              </button>
            </div>
          </div>
        </div>
      )}

      {showVerifyModal && pendingVerification && (
        <VerifyBatchModal
          counts={pendingVerification.counts}
          leadRows={pendingVerification.leadRows}
          billingType={(billingType === 'per_contact' ? 'per_contact' : 'per_call') as 'per_call' | 'per_contact'}
          defaultCellOnly={cellOnly}
          onSkip={() => {
            // Close the modal IMMEDIATELY so the user isn't blocked.
            // Queue the unconfirmed leads in the background — addDeferredVerification
            // can take 30+ seconds for large batches and we don't want the modal
            // hanging around during the writes.
            const rowCount = pendingVerification.leadRows.length;
            const rowsToQueue = pendingVerification.leadRows;
            setShowVerifyModal(false);
            setPendingVerification(null);
            setFile(null); setRawRows([]); setNoHeader(false);
            setHeaders([]); setPreview([]); setAllRows([]); setProgress(''); setUploadLabel('');
            setResult(`Saving ${rowCount.toLocaleString()} unverified records to your queue…`);

            // Fire-and-forget — the modal is already closed
            (async () => {
              try {
                const fn = httpsCallable(functions, 'addDeferredVerification');
                await fn({ leadRows: rowsToQueue });
                setResult(`Saved ${rowCount.toLocaleString()} unverified records. You can run them anytime from your dashboard.`);
                // Same auto-redirect as the immediate-write path — let the
                // user see the success message, then send them to the
                // dashboard where the deferred-queue counter is visible.
                window.setTimeout(() => navigate('/'), POST_UPLOAD_REDIRECT_MS);
              } catch (err) {
                console.error('Failed to queue deferred:', err);
                setResult('Error: unverified records could not be saved — please re-upload to retry.');
              }
            })();
          }}
          onSubmitted={() => {
            // Stripe redirect is happening — clean up local state
            setShowVerifyModal(false);
            setPendingVerification(null);
            setFile(null); setRawRows([]); setNoHeader(false);
            setHeaders([]); setPreview([]); setAllRows([]); setProgress(''); setUploadLabel('');
          }}
        />
      )}
    </div>
  );
}
